import { isTRPCClientError } from '@trpc/client';

import { isAuthFlowPathname } from '#utils/auth-flow-routes.js';
import { StaleDeployError } from './stale-deploy-error.js';

/** How the page recovers from a chunk that failed to load. */
export enum ChunkLoadRecoveryOutcome {
	/** The page reloads itself to pick up the build the server is serving now. */
	Reloaded = 'reloaded',
	/** The page asks the user to reload, because an automatic reload isn't allowed right now. */
	Fallback = 'fallback',
}

/**
 * Message prefixes for a lazily loaded module or stylesheet that failed to load: Chromium's, Firefox's and
 * Safari's for `import()`, then Vite's for the CSS it preloads alongside a chunk.
 */
const CHUNK_LOAD_ERROR_PREFIXES = [
	'Failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'Importing a module script failed',
	'Unable to preload CSS for',
];

/** The message prefix tRPC's server gives a procedure path it has no route for. */
const MISSING_PROCEDURE_MESSAGE_PREFIX = 'No procedure found on path';

/** The `sessionStorage` key holding when this tab last reloaded itself after a chunk failed to load. */
const AUTO_RELOAD_AT_KEY = 'cadstrom:chunk-load-auto-reload-at';

/** The minimum time between automatic reloads in one tab, so a chunk that keeps failing can't loop the page. */
const AUTO_RELOAD_COOLDOWN_MS = 30_000;

const outcomes = new WeakMap<Error, ChunkLoadRecoveryOutcome>();

/** The outcome of the stale-deploy failure last reported outside an error boundary, if one is outstanding. */
let reportedOutcome: ChunkLoadRecoveryOutcome | undefined;
/** The error behind the reported outcome. */
let reportedError: unknown;
/** How many error boundaries are showing a stale-deploy display right now. */
let boundaryDisplayCount = 0;
const reportedOutcomeListeners = new Set<() => void>();
/** Errors whose automatic reload has already started, so a second display of one doesn't reload again. */
const reloadedErrors = new WeakSet<object>();

/**
 * Starts this tab's auto-reload cooldown, unless the current route or a cooldown still running forbids it.
 *
 * @returns True when the claim was recorded and the page may reload itself.
 */
function claimAutoReload(): boolean {
	if (isAuthFlowPathname(window.location.pathname)) {
		return false;
	}

	try {
		const lastReloadAt = Number(sessionStorage.getItem(AUTO_RELOAD_AT_KEY));
		const now = Date.now();
		// A missing or garbled value parses to 0 or NaN, and neither falls inside the cooldown.
		if (now - lastReloadAt < AUTO_RELOAD_COOLDOWN_MS) {
			return false;
		}
		sessionStorage.setItem(AUTO_RELOAD_AT_KEY, String(now));
		return true;
	} catch {
		return false;
	}
}

/**
 * Whether an error is a tRPC call to a procedure the server doesn't have, as happens when the running page
 * belongs to a build whose procedures have since been renamed or removed. A procedure's own `NOT_FOUND`, such
 * as for a missing record, doesn't count.
 *
 * @param error - The caught value.
 * @returns True for a call to a missing procedure.
 */
export function isMissingTrpcProcedureError(error: unknown): error is Error {
	if (!isTRPCClientError(error)) {
		return false;
	}
	const data: unknown = error.data;
	const code = typeof data === 'object' && data !== null && 'code' in data ? data.code : undefined;
	return code === 'NOT_FOUND' && error.message.startsWith(MISSING_PROCEDURE_MESSAGE_PREFIX);
}

/**
 * Whether an error shows the running page belongs to a build the server no longer serves: a lazily loaded
 * chunk (or its stylesheet) or a worker script failing to load, or a tRPC call to a procedure the server
 * doesn't have.
 *
 * @param error - The caught value.
 * @returns True for a stale-deploy failure.
 */
export function isChunkLoadError(error: unknown): error is Error {
	return (
		error instanceof StaleDeployError ||
		isMissingTrpcProcedureError(error) ||
		(error instanceof Error && CHUNK_LOAD_ERROR_PREFIXES.some((prefix) => error.message.startsWith(prefix)))
	);
}

/**
 * Decides how the page recovers from an error, once per error object: asking again about the same error
 * returns the same outcome.
 *
 * `Reloaded` claims this tab's automatic reload, so no other chunk failure gets one until the cooldown
 * passes. It is never given on the login/logout routes, which redirect on their own, or when
 * `sessionStorage` is unavailable, since the cooldown couldn't be enforced across the reload.
 *
 * @param error - The caught value.
 * @returns The outcome, or `undefined` when the error isn't a chunk-load failure.
 */
export function resolveChunkLoadRecovery(error: unknown): ChunkLoadRecoveryOutcome | undefined {
	if (!isChunkLoadError(error)) {
		return undefined;
	}

	let outcome = outcomes.get(error);
	if (outcome === undefined) {
		outcome = claimAutoReload() ? ChunkLoadRecoveryOutcome.Reloaded : ChunkLoadRecoveryOutcome.Fallback;
		outcomes.set(error, outcome);
	}
	return outcome;
}

/**
 * Recovers from an error that may never reach an error boundary, such as one a component catches or a query
 * that doesn't throw. A stale-deploy failure is resolved as {@link resolveChunkLoadRecovery} does and becomes
 * the reported outcome, unless one is already outstanding; any other error is ignored.
 *
 * @param error - The caught value.
 */
export function reportChunkLoadError(error: unknown): void {
	if (reportedOutcome !== undefined) {
		return;
	}
	const outcome = resolveChunkLoadRecovery(error);
	if (outcome === undefined) {
		return;
	}
	reportedOutcome = outcome;
	reportedError = error;
	notifyReportedOutcomeListeners();
}

/**
 * Tells listeners that the reported outcome, or whether it should be shown, may have changed.
 */
function notifyReportedOutcomeListeners(): void {
	for (const listener of reportedOutcomeListeners) {
		listener();
	}
}

/**
 * The reported outcome to show outside the error boundaries: none while a boundary already shows a
 * stale-deploy display, so the failure isn't shown twice.
 *
 * @returns The outcome, or `undefined` when nothing should be shown.
 */
export function getUnshownReportedChunkLoadRecovery(): ChunkLoadRecoveryOutcome | undefined {
	return boundaryDisplayCount > 0 ? undefined : reportedOutcome;
}

/**
 * The error behind the outstanding reported failure.
 *
 * @returns The error, or `undefined` when none is outstanding.
 */
export function getReportedChunkLoadError(): unknown {
	return reportedError;
}

/**
 * Records that an error boundary is showing a stale-deploy display, until the returned function is called.
 *
 * @returns A function that ends the registration; calling it again has no further effect.
 */
export function registerBoundaryChunkLoadDisplay(): () => void {
	boundaryDisplayCount += 1;
	notifyReportedOutcomeListeners();
	let registered = true;
	return () => {
		if (!registered) {
			return;
		}
		registered = false;
		boundaryDisplayCount -= 1;
		notifyReportedOutcomeListeners();
	};
}

/**
 * Claims the automatic reload for an error, so any number of displays of one error reload the page once.
 *
 * @param error - The error being displayed; `undefined` or a non-object always grants the claim.
 * @returns True when the caller should reload.
 */
export function claimReloadForError(error: unknown): boolean {
	if (typeof error !== 'object' || error === null) {
		return true;
	}
	if (reloadedErrors.has(error)) {
		return false;
	}
	reloadedErrors.add(error);
	return true;
}

/**
 * The outcome of the outstanding reported stale-deploy failure.
 *
 * @returns The outcome, or `undefined` when none is outstanding.
 */
export function getReportedChunkLoadRecovery(): ChunkLoadRecoveryOutcome | undefined {
	return reportedOutcome;
}

/**
 * Clears the outstanding reported failure, so the next one is reported afresh.
 */
export function dismissReportedChunkLoadRecovery(): void {
	if (reportedOutcome === undefined) {
		return;
	}
	reportedOutcome = undefined;
	reportedError = undefined;
	notifyReportedOutcomeListeners();
}

/**
 * Listens for changes to the reported outcome.
 *
 * @param listener - Called after each change.
 * @returns A function that stops listening.
 */
export function subscribeToReportedChunkLoadRecovery(listener: () => void): () => void {
	reportedOutcomeListeners.add(listener);
	return () => {
		reportedOutcomeListeners.delete(listener);
	};
}

/**
 * Reloads the page from the server.
 */
export function reloadPage(): void {
	window.location.reload();
}
